import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { RadioButton } from 'primereact/radiobutton';
import { useToast } from '../../../components/Toast';
import { Calendar } from 'primereact/calendar';
import RequiredLabel from '../../../components/RequiredLabel';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import { useDialog } from '../../../components/Dialog';
import qrService from './qr-service';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { QRCodeCanvas } from 'qrcode.react';
import { exportQrPDF } from '../../../components/Export';


interface DropdownItem {
    referenceCode: string;
    fullDesc: string;
}

const QRView: React.FC = () => {
    const [clientState, setClientState] = useState<any>(null);
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const { showToast } = useToast();
    const { showExceptionDialog, showConfirmRemarkDialog, showConfirmDialog } = useDialog();
    const location = useLocation();
    const selectedItem = location.state?.selectedItem;
    const isEdit = !!selectedItem;
    const [ASTQrdetCollection, setASTQrdetCollection] = useState<any[]>([]);


    const [formData, setFormData] = useState<any>(null);

    const defaultData = {
    };

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                qrService.getClientState().then((data) => {
                    setClientState(data);
                });

                if (isEdit) {
                    setFormData(selectedItem);

                    qrService.findAllQRdet(selectedItem.qrBatchId).then(async (data) => {
                        if (data) {
                            const qrdetData = await Promise.all(data.map(async (qrdet: any) => {
                                let instrument;
                                if (qrdet.instrumentId) {
                                    instrument = await qrService.findOneInstrument(qrdet.instrumentId);
                                }
                                return { ...qrdet, instrumentHasOne: instrument };
                            }));

                            setASTQrdetCollection(qrdetData);
                        }
                    });

                } else {
                    setFormData(defaultData);
                };


            } catch (error) {
                clientState.error.detail = error;
                showToast(clientState.error);
            }
        };

        fetchClientState();
    }, []);

    const renderQRCode = (rowData: any) => {
        return (
            <div className="flex justify-center p-4">
                <QRCodeCanvas value={rowData.qrValue} size={80} />
            </div>
        );
    };

    const header = () => {

        const formatDate = (date: any): string => {
            if (!date || typeof date === "boolean") return "Invalid Date";
            return new Date(date).toLocaleDateString("en-GB");
        };

        const fileName = `QR Generation`;
        return (
            <div>
                <div className="flex justify-content-between">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.3em' }}>
                        <span className="p-float-label">
                        </span>
                    </div>
                    <div>
                        <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger" style={{ marginRight: '.5em' }} onClick={() => exportQrPDF(fileName)} />
                    </div>
                </div>
            </div>
        );
    };

    const footerTemplate = () => {

        function handleBack(): void {
            navigate(`/machine/qr`);
        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Back" icon="pi pi-reply" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    return (
        <div className="grid">
            <div className='col-12'>
                <div className='card-title'>
                    <h5>QR Generation View</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend="QR Information">
                        <div className="p-fluid form-grid grid">


                        </div>
                        <div className="my-custom-table">
                            <DataTable
                                value={ASTQrdetCollection}
                                editMode="row"
                                dataKey="id"
                                showGridlines
                                scrollable
                                scrollHeight="auto"
                                sortOrder={-1}
                                header={header}
                            >
                                {/* Row Numbering */}
                                <Column
                                    header="No."
                                    style={{ width: "5%" }}
                                    body={(_, { rowIndex }) => rowIndex + 1}
                                    headerClassName="header-grid-style"
                                ></Column>

                                {/* Data Columns */}
                                <Column headerClassName="header-grid-style" field="insertDate" header="Register Date" style={{ width: "20%" }} body={(rowData) => new Date(rowData.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })}></Column>
                                <Column headerClassName="header-grid-style" field="qrId" header="QR Id" style={{ width: "20%" }}></Column>
                                <Column
                                    field="qrValue"
                                    headerClassName="header-grid-style"
                                    header="QR"
                                    body={(rowData) => renderQRCode(rowData)}
                                    style={{ width: "20%" }}
                                />
                                <Column headerClassName="header-grid-style" field="assignBy" header="Assign By" style={{ width: "20%" }}></Column>
                                <Column headerClassName="header-grid-style" field="assignDate" header="Assign Date" style={{ width: "20%" }}
                                    body={(rowData) => rowData.assignDate
                                        ? new Date(rowData.assignDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })
                                        : ''
                                    }>
                                </Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.itemDesc" header="Instrument Name" style={{ width: "20%" }}></Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.snNo" header="Instrument S/N" style={{ width: "20%" }}></Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.itemCode" header="Instrument Code" style={{ width: "20%" }}></Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.manufacturerName" header="Manufacturer Name" style={{ width: "20%" }}></Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.lastPmDate" header="Last PM Date" style={{ width: "20%" }}
                                    body={(rowData) => rowData.instrumentHasOne?.lastPmDate
                                        ? new Date(rowData.instrumentHasOne.lastPmDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })
                                        : ''
                                    }>
                                </Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.nextPmDate" header="Next PM Date" style={{ width: "20%" }}
                                    body={(rowData) => rowData.instrumentHasOne?.nextPmDate
                                        ? new Date(rowData.instrumentHasOne.nextPmDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })
                                        : ''
                                    }>
                                </Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.installDate" header="Install Date" style={{ width: "20%" }}
                                    body={(rowData) => rowData.instrumentHasOne?.installDate
                                        ? new Date(rowData.instrumentHasOne.installDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })
                                        : ''
                                    }>
                                </Column>
                                <Column headerClassName="header-grid-style" field="instrumentHasOne.warrantyEndDate" header="Warranty End Date" style={{ width: "20%" }}
                                    body={(rowData) => rowData.instrumentHasOne?.warrantyEndDate
                                        ? new Date(rowData.instrumentHasOne.warrantyEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })
                                        : ''
                                    }>
                                </Column>
                            </DataTable>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={footerTemplate}></Toolbar>
                </div>
            </div>
        </div>

    );
};

export default QRView;
